import {
  organizationQueryKey,
  useMutationCreateOrganization,
  useMutationUpdateOrganization,
  useQueryGetOneOrganization,
} from "@apis:organization/organization";
import {
  organizationAdminQueryKey,
  useMutationInsertDeleteOrganizationAdmins,
  useQueryGetAllOrganizationAdmins,
} from "@apis:organization_admin/organization_admin";
import { useQueryGetAllUsers } from "@apis:user/user";
import { SuperDialog } from "@components:SuperDialog";
import { SuperInput } from "@components:SuperInput/SuperInput";
import { useInvalidateQueries } from "@hooks:useInvalidateQueries";
import { useRBACCheck } from "@hooks:useRBAC";
import { Box, CircularProgress, TextField } from "@mui/material";
import { useToasterStoreActions } from "@stores:global";
import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";

import { FeatureFlag } from "@:flag";
import { RBACAction, RBACResource } from "@:rbac";

interface OrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId?: string;
}

export const OrganizationModal = ({
  isOpen,
  onClose,
  organizationId,
}: OrganizationModalProps) => {
  const canManipulateUser = useRBACCheck(
    RBACAction.read,
    RBACResource["settings__users"],
  );

  const invalidateQueries = useInvalidateQueries();
  const toastActions = useToasterStoreActions();
  const { data, isLoading } = useQueryGetOneOrganization(
    { id: organizationId! },
    {
      enabled: !!organizationId,
    },
  );

  const { data: orgAdmins, isLoading: isOrgAdminsLoading } =
    useQueryGetAllOrganizationAdmins(
      { organizationId: organizationId! },
      {
        enabled: !!organizationId,
      },
    );

  const { register, handleSubmit, formState, control } = useForm({
    defaultValues: {
      name: "",
      enabled: true,
      user_ids: [],
    },
    values: {
      name: data?.name,
      enabled: data?.enabled,
      user_ids: orgAdmins?.map((admin) => admin.userId) || [],
    },
    // we need to use mode: 'all' for MUI https://github.com/orgs/react-hook-form/discussions/2357#discussioncomment-42073
    mode: "all",
  });

  const mutationCreateOrganization = useMutationCreateOrganization({
    onSuccess: async () => {
      toastActions.setToaster({
        open: true,
        message: "Organization created",
        variant: "success",
      });
      await invalidateQueries.run([
        {
          queryKey: organizationQueryKey.getAll.queryKey,
        },
      ]);
    },
    onError: () => {
      toastActions.setToaster({
        open: true,
        message: "Failed to create organization",
        variant: "error",
      });
    },
  });

  const mutationUpdateOrganization = useMutationUpdateOrganization({
    onSuccess: async () => {
      toastActions.setToaster({
        open: true,
        message: "Organization updated",
        variant: "success",
      });
      await invalidateQueries.run([
        {
          queryKey: organizationQueryKey.getAll.queryKey,
        },
      ]);
    },
    onError: () => {
      toastActions.setToaster({
        open: true,
        message: "Failed to update organization",
        variant: "error",
      });
    },
  });

  const mutationCreateDeleteOrganizationAdmins =
    useMutationInsertDeleteOrganizationAdmins({
      onSuccess: async () => {
        await invalidateQueries.run([
          {
            queryKey: organizationAdminQueryKey.getAll._def,
          },
        ]);
      },
    });

  const { data: users, isLoading: usersLoading } = useQueryGetAllUsers({
    enabled: canManipulateUser,
  });

  const usersOptions = useMemo(
    () =>
      users?.map((user) => ({
        value: user.id,
        label: user.name,
      })) || [],
    [users],
  );

  return (
    <SuperDialog
      open={isOpen}
      onClose={onClose}
      title={organizationId ? "Edit Organization" : "Add Organization"}
      isLoading={isLoading || isOrgAdminsLoading}
      sx={{
        "& .MuiDialog-paper": {
          height: "30rem",
          width: "30rem",
        },
      }}
      actionControls={{
        secondary: {
          label: "Cancel",
          onClick: () => {
            onClose();
          },
          sx: {
            width: "5rem",
          },
        },
        primary: {
          sx: {
            width: "5rem",
          },
          label: "Save",
          onClick: async () => {
            await handleSubmit(async (data) => {
              if (organizationId) {
                await mutationUpdateOrganization.mutateAsync({
                  id: organizationId,
                  ...data,
                });

                if (Array.isArray(data.user_ids)) {
                  await mutationCreateDeleteOrganizationAdmins.mutateAsync({
                    organizationId: organizationId,
                    userIds: data.user_ids,
                  });
                }
              } else {
                const createdOrg = await mutationCreateOrganization.mutateAsync(
                  {
                    name: data.name!,
                    ...(FeatureFlag.UpdateEnabled
                      ? { enabled: data.enabled! }
                      : {}),
                  },
                );

                if (Array.isArray(data.user_ids)) {
                  await mutationCreateDeleteOrganizationAdmins.mutateAsync({
                    organizationId: createdOrg.id,
                    userIds: data.user_ids,
                  });
                }
              }
              onClose();
            })();
          },
          disabled: !formState.isValid,
        },
      }}
    >
      <Box>
        <SuperInput.TextField
          required
          label="Name"
          {...register("name", {
            required: "Name is required",
          })}
          errorText={formState.errors.name?.message}
        />
        {FeatureFlag.UpdateEnabled && (
          <Controller
            name="enabled"
            defaultValue={true}
            control={control}
            render={({ field }) => (
              <SuperInput.Switch
                required
                sx={{
                  marginTop: "10px",
                }}
                label="Enabled"
                checked={field.value}
                onChange={field.onChange}
              />
            )}
          />
        )}
        <Controller
          name="user_ids"
          control={control}
          render={({ field }) => {
            const selectedValue =
              usersOptions.filter((option) =>
                field?.value?.includes(option.value),
              ) ?? [];
            return (
              <SuperInput.Autocomplete
                sx={{
                  flex: 1,
                  marginTop: "1rem",
                }}
                multiple
                clearOnBlur={false}
                loading={usersLoading}
                options={usersOptions}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                getOptionLabel={(option) => option.label}
                renderInput={(params) => {
                  return (
                    <TextField
                      {...params}
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {usersLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        },
                      }}
                    />
                  );
                }}
                {...field}
                value={selectedValue}
                onChange={(_, value) => {
                  field.onChange(value?.map((item: any) => item.value));
                }}
                noOptionsText={"No user found"}
                width="100%"
                label="Admin Users"
                errorText={formState.errors.user_ids?.message}
              />
            );
          }}
        />
      </Box>
    </SuperDialog>
  );
};
